import { useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';

interface ErrorReport {
  error: Error | string;
  context: {
    page: string;
    action: string;
    userId?: string;
    userAgent: string;
    timestamp: string;
    url: string;
    component?: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
    metadata?: Record<string, any>;
  };
}

interface ErrorMonitoringConfig {
  enabled?: boolean;
  onlyProduction?: boolean;
  debounceMs?: number;
  maxReportsPerMinute?: number;
}

export const useErrorMonitoring = (config: ErrorMonitoringConfig = {}) => {
  const reportCount = useRef(0);
  const lastResetTime = useRef(Date.now());

  const {
    enabled = true,
    onlyProduction = true,
    debounceMs = 1000,
    maxReportsPerMinute = 1 // Aggressive: only 1 alert per period
  } = config;

  const shouldReport = useCallback(() => {
    // Only report on production domain if enabled
    if (onlyProduction && !window.location.hostname.includes('vercel.app')) {
      return false;
    }

    // Rate limiting - reset counter every 15 minutes (aggressive)
    const now = Date.now();
    if (now - lastResetTime.current > 900000) { // 15 minutes
      reportCount.current = 0;
      lastResetTime.current = now;
    }

    if (reportCount.current >= maxReportsPerMinute) {
      if (import.meta.env.DEV) {
        console.warn('Error reporting rate limit exceeded');
      }
      return false;
    }

    reportCount.current++;
    return enabled;
  }, [enabled, onlyProduction, maxReportsPerMinute]);

  const reportError = useCallback(async (errorReport: ErrorReport) => {
    if (!shouldReport()) return;

    try {
      // Get current user if available
      const { data: { user } } = await supabase.auth.getUser();

      // Enhance context with user info
      const enhancedReport = {
        ...errorReport,
        context: {
          ...errorReport.context,
          userId: user?.id,
          sessionId: (await supabase.auth.getSession()).data.session?.access_token?.substring(0, 10),
        }
      };

      // Report to Sentry with full context
      const errorToReport = typeof errorReport.error === 'string'
        ? new Error(errorReport.error)
        : errorReport.error;

      Sentry.captureException(errorToReport, {
        contexts: {
          errorContext: {
            page: errorReport.context.page,
            action: errorReport.context.action,
            component: errorReport.context.component,
            userAgent: errorReport.context.userAgent,
            url: errorReport.context.url,
          },
        },
        tags: {
          severity: errorReport.context.severity,
          component: errorReport.context.component,
          action: errorReport.context.action,
        },
        level: errorReport.context.severity === 'critical' ? 'fatal' :
          errorReport.context.severity === 'high' ? 'error' :
            errorReport.context.severity === 'medium' ? 'warning' : 'info',
        user: user ? {
          id: user.id,
          email: user.email,
        } : undefined,
        extra: errorReport.context.metadata,
      });

      // Add breadcrumb for future errors
      Sentry.addBreadcrumb({
        category: errorReport.context.component || 'unknown',
        message: `${errorReport.context.action} failed`,
        level: errorReport.context.severity === 'critical' ? 'fatal' : 'error',
        data: errorReport.context.metadata,
      });

      // Call edge function to send alert email (silently - no user notification)
      const { error } = await supabase.functions.invoke('alert-critical-error', {
        body: enhancedReport
      });

      if (error) {
        console.error('Failed to send error alert:', error);
      }

      // Log to audit system for tracking
      await supabase.from('audit_logs').insert({
        action: 'critical_error_reported',
        table_name: 'error_monitoring',
        new_values: {
          error_message: typeof errorReport.error === 'string'
            ? errorReport.error
            : errorReport.error.message,
          page: errorReport.context.page,
          action: errorReport.context.action,
          severity: errorReport.context.severity,
          component: errorReport.context.component,
          metadata: errorReport.context.metadata
        }
      });

    } catch (err) {
      console.error('Error monitoring system failed:', err);
    }
  }, [shouldReport]);

  const reportCriticalError = useCallback((
    error: Error | string,
    page: string,
    action: string,
    component?: string,
    metadata?: Record<string, any>
  ) => {
    const errorReport: ErrorReport = {
      error,
      context: {
        page,
        action,
        component,
        severity: 'critical',
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        metadata
      }
    };

    // Report error silently - no toast notification for users
    reportError(errorReport);
  }, [reportError]);

  const reportPaymentError = useCallback((error: Error | string, metadata?: Record<string, any>) => {
    reportCriticalError(error, window.location.pathname, 'payment_processing', 'PaymentForm', metadata);
  }, [reportCriticalError]);

  const reportAuthError = useCallback((error: Error | string, authAction: string, metadata?: Record<string, any>) => {
    reportCriticalError(error, window.location.pathname, `auth_${authAction}`, 'AuthModal', metadata);
  }, [reportCriticalError]);

  const reportDatabaseError = useCallback((error: Error | string, operation: string, metadata?: Record<string, any>) => {
    reportCriticalError(error, window.location.pathname, `database_${operation}`, 'DatabaseOperation', metadata);
  }, [reportCriticalError]);

  const reportAPIError = useCallback((error: Error | string, endpoint: string, metadata?: Record<string, any>) => {
    reportCriticalError(error, window.location.pathname, `api_${endpoint}`, 'APICall', metadata);
  }, [reportCriticalError]);

  return {
    reportError,
    reportCriticalError,
    reportPaymentError,
    reportAuthError,
    reportDatabaseError,
    reportAPIError,
    isEnabled: enabled && shouldReport()
  };
};